const responseCache = new Map();

function getReputation(endpoint) {
  if (!responseCache.has(endpoint)) {
    const request = fetch(endpoint, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Review data request failed (${response.status})`);
        return response.json();
      })
      .then((payload) => {
        if (!payload?.ok || !payload.platforms) throw new Error('Review data response was invalid');
        return payload;
      })
      .catch((error) => {
        responseCache.delete(endpoint);
        throw error;
      });
    responseCache.set(endpoint, request);
  }
  return responseCache.get(endpoint);
}

function toRating(value) {
  const rating = Number(value);
  return Number.isFinite(rating) && rating > 0 && rating <= 5 ? rating : null;
}

function toCount(value) {
  const count = Number(value);
  return Number.isSafeInteger(count) && count >= 0 ? count : null;
}

function platformRecord(payload, platform) {
  if (Array.isArray(payload.platforms)) {
    return payload.platforms.find((item) => item?.platform === platform) || null;
  }
  return payload.platforms?.[platform] || null;
}

function formatCount(count, platform, format) {
  if (format === 'short') return `${count.toLocaleString('en-GB')} ${count === 1 ? 'review' : 'reviews'}`;
  const platformName = platform === 'google' ? 'Google' : 'Trustpilot';
  if (count === 1) return `Based on 1 ${platformName} review`;
  return `Based on ${count.toLocaleString('en-GB')} ${platformName} reviews`;
}

function updateStars(container, rating, platform) {
  const stars = container.querySelector('[data-review-stars]');
  if (!stars) return;
  stars.style.setProperty('--cp-review-fill', `${rating * 20}%`);
  stars.setAttribute('aria-label', `${rating.toFixed(1)} out of 5 stars on ${platform}`);
}

function updatePlatform(container, platform, record) {
  const rating = toRating(record?.rating);
  const count = toCount(record?.reviewCount ?? record?.review_count);
  if (rating === null) return false;

  const ratingOutput = container.querySelector('[data-review-rating]');
  const countOutput = container.querySelector('[data-review-count]');
  if (ratingOutput) ratingOutput.textContent = rating.toFixed(1);
  if (countOutput) {
    const format = countOutput.dataset.reviewCountFormat;
    if (count !== null && count > 0) countOutput.textContent = formatCount(count, platform, format);
    // Short counts sit inside a sentence, so keep the saved count rather than a call to action.
    else if (format !== 'short') countOutput.textContent = `Read our ${platform === 'google' ? 'Google' : 'Trustpilot'} reviews`;
  }
  updateStars(container, rating, platform);
  return true;
}

function reviewDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric' }).format(date);
}

function reviewerFirstName(value, fallback) {
  const name = String(value || '').trim();
  return name ? name.split(/\s+/)[0] : fallback;
}

function reviewContentKey(value) {
  return String(value || '')
    .normalize('NFKC')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLocaleLowerCase('en-GB');
}

function reviewIdentityKey(platform, id) {
  const value = String(id || '').trim();
  return value ? `${platform}:id:${value}` : '';
}

// Older saved cards have no provider ID and sometimes shorten the original quote.
// Require both the same name and the complete shorter quote, never either alone.
// Google feed reviews from the newest-first list or the Business Profile API
// still match a saved card that has a Places review ID.
function matchesLegacyReview(card, platform, reviewer, content, ignoreSavedId = false) {
  if ((card.dataset.reviewId && !ignoreSavedId) || card.dataset.reviewCardPlatform !== platform) return false;
  const name = reviewContentKey(reviewerFirstName(reviewer, ''));
  const savedName = reviewContentKey(reviewerFirstName(card.querySelector('.cp-review-card__reviewer')?.textContent, ''));
  if (!name || name !== savedName) return false;
  const quoteKey = (value) => reviewContentKey(value).replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const quote = quoteKey(content);
  const savedQuote = quoteKey(card.querySelector('.cp-review-card__quote')?.textContent);
  return Boolean(quote && savedQuote && (
    quote === savedQuote || quote.startsWith(`${savedQuote} `) || savedQuote.startsWith(`${quote} `)
  ));
}

function reviewMonth(value) {
  const timestamp = Date.parse(value || '');
  if (Number.isNaN(timestamp)) return null;
  const date = new Date(timestamp);
  return date.getUTCFullYear() * 12 + date.getUTCMonth();
}

function reviewerNameParts(value) {
  return reviewContentKey(value).replace(/[^\p{L}\p{N}\s'-]+/gu, ' ').split(/\s+/).filter(Boolean);
}

function quoteWords(value) {
  return new Set(reviewContentKey(value).replace(/[^\p{L}\p{N}]+/gu, ' ').split(' ').filter((word) => word.length > 2));
}

function quoteOverlap(left, right) {
  const a = quoteWords(left);
  const b = quoteWords(right);
  if (!a.size || !b.size) return 0;
  let shared = 0;
  a.forEach((word) => { if (b.has(word)) shared += 1; });
  return shared / Math.min(a.size, b.size);
}

function sameReviewer(savedName, feedName) {
  const saved = reviewerNameParts(savedName);
  const feed = reviewerNameParts(feedName);
  if (!saved.length || !feed.length) return false;
  // With both surnames known they must agree, and one first name must start the other (Jo / Josey).
  if (saved.length > 1 && feed.length > 1) {
    return saved.at(-1) === feed.at(-1) && (saved[0].startsWith(feed[0]) || feed[0].startsWith(saved[0]));
  }
  return saved[0] === feed[0];
}

// Saved homepage cards hold tidied copies of reviews ("two" for "2", shortened
// sentences), so a review that arrives under a different provider ID is matched
// to its saved card by reviewer and month (one month either side), and by the
// closest quote only when several saved cards fit.
function findSavedReviewerCard(cards, platform, review, claimed) {
  const month = reviewMonth(review?.publishedAt);
  if (month === null || !review?.reviewerName) return null;
  const candidates = cards.filter((card) => {
    if (card.dataset.reviewCardPlatform !== platform || claimed.has(card)) return false;
    const savedMonth = reviewMonth(card.dataset.reviewPublishedAt);
    if (savedMonth === null || Math.abs(savedMonth - month) > 1) return false;
    return sameReviewer(
      card.dataset.reviewReviewer || card.querySelector('.cp-review-card__reviewer')?.textContent,
      review.reviewerName,
    );
  });
  if (candidates.length <= 1) return candidates[0] || null;
  const quote = review.excerpt || review.title;
  const ranked = candidates
    .map((card) => ({ card, score: quoteOverlap(card.querySelector('.cp-review-card__quote')?.textContent, quote) }))
    .sort((left, right) => right.score - left.score);
  return ranked[0].score >= 0.5 && ranked[0].score > ranked[1].score ? ranked[0].card : null;
}

function reviewTimestamp(value) {
  const timestamp = Date.parse(value || '');
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function sortRenderedCards(track) {
  const cards = [...track.querySelectorAll('.cp-review-card')];
  cards
    .map((card, index) => ({ card, index }))
    .sort((left, right) => (
      reviewTimestamp(right.card.dataset.reviewPublishedAt)
      - reviewTimestamp(left.card.dataset.reviewPublishedAt)
      || left.index - right.index
    ))
    .forEach(({ card }) => track.append(card));
}

function reviewPhoto(review) {
  const photo = Array.isArray(review?.photos) ? review.photos[0] : null;
  const url = String(photo?.url || '').trim();
  return /^https:\/\//i.test(url) ? { ...photo, url } : null;
}

function photoDimension(value) {
  const size = Number(value);
  return Number.isSafeInteger(size) && size > 0 ? size : null;
}

// The customer's own photo of their print, loaded from Google's URL (never re-hosted)
// and shown inside the card that links to the review it came with.
function attachReviewPhoto(card, review, reviewerName) {
  const photo = reviewPhoto(review);
  if (!photo || card.querySelector('.cp-review-card__photo')) return;
  const figure = document.createElement('figure');
  figure.className = 'cp-review-card__photo';
  const image = document.createElement('img');
  image.src = photo.url;
  image.alt = `Customer photo from ${reviewerName}'s review`;
  image.loading = 'lazy';
  image.decoding = 'async';
  const width = photoDimension(photo.widthPx);
  const height = photoDimension(photo.heightPx);
  if (width && height) {
    image.width = width;
    image.height = height;
  }
  figure.append(image);
  const quote = card.querySelector('.cp-review-card__quote');
  if (quote) quote.before(figure);
  else card.append(figure);
  card.classList.add('cp-review-card--photo');
}

function createReviewCard(review, fallbackUrls, logos, options = {}) {
  const platform = review?.platform === 'trustpilot' ? 'trustpilot' : 'google';
  const platformName = platform === 'trustpilot' ? 'Trustpilot' : 'Google';
  const destination = review?.sourceUrl || fallbackUrls[platform];
  const logo = logos[platform];
  const reviewerName = reviewerFirstName(review?.reviewerName, `${platformName} customer`);
  const card = document.createElement(destination ? 'a' : 'article');
  card.className = 'cp-review-card';
  card.dataset.reviewCardPlatform = platform;
  card.dataset.reviewId = String(review?.id || '').trim();
  card.dataset.reviewPublishedAt = review?.publishedAt || '';
  if (destination) {
    card.href = destination;
    card.target = '_blank';
    card.rel = 'noopener noreferrer';
    card.dataset.homepageEvent = 'reviews_source_click';
    card.dataset.homepageValue = platform;
    card.setAttribute('aria-label', `Read ${reviewerName}'s review on ${platformName}`);
  }

  const meta = document.createElement('div');
  meta.className = 'cp-review-card__meta';
  const stars = document.createElement('span');
  stars.className = 'cp-review-stars';
  stars.setAttribute('role', 'img');
  const rating = toRating(review?.rating) || 5;
  stars.setAttribute('aria-label', `${rating} out of 5 stars on ${platformName}`);
  stars.style.setProperty('--cp-review-fill', `${rating * 20}%`);
  stars.innerHTML = '<span class="cp-review-stars__empty" aria-hidden="true">★★★★★</span><span class="cp-review-stars__fill" aria-hidden="true">★★★★★</span>';

  const platformBadge = document.createElement('span');
  platformBadge.className = 'cp-review-card__platform';
  if (logo) {
    const platformLogo = logo.cloneNode(true);
    platformLogo.classList.remove('cp-review-widget__logo');
    platformLogo.classList.add('cp-review-card__platform-logo');
    platformBadge.append(platformLogo);
  }
  const platformLabel = document.createElement('span');
  platformLabel.textContent = platformName;
  platformBadge.append(platformLabel);
  meta.append(stars, platformBadge);

  const quote = document.createElement('p');
  quote.className = 'cp-review-card__quote';
  quote.textContent = review?.excerpt || review?.title || '';

  const footer = document.createElement('footer');
  footer.className = 'cp-review-card__footer';
  const reviewer = document.createElement('span');
  reviewer.className = 'cp-review-card__reviewer';
  reviewer.textContent = reviewerName;
  const date = document.createElement('span');
  date.className = 'cp-review-card__date';
  date.textContent = reviewDate(review?.publishedAt);
  footer.append(reviewer, date);
  card.append(meta, quote, footer);
  if (options.showPhotos) attachReviewPhoto(card, review, reviewerName);

  return card;
}

class ReviewDataElement extends HTMLElement {
  connectedCallback() {
    if (this.reviewConnection) return;
    const connection = {};
    this.reviewConnection = connection;
    const endpoint = this.dataset.endpoint;
    if (!endpoint) return;
    getReputation(endpoint).then((payload) => {
      if (this.isConnected && this.reviewConnection === connection) this.applyPayload(payload);
    }).catch(() => {
      // Saved ratings are already rendered server-side, so a failed refresh needs no notice.
    });
  }

  disconnectedCallback() {
    this.reviewConnection = null;
  }

  applyPayload(payload) {
    let updated = false;
    this.querySelectorAll('[data-review-platform]').forEach((container) => {
      const platform = container.dataset.reviewPlatform;
      updated = updatePlatform(container, platform, platformRecord(payload, platform)) || updated;
    });
    if (updated) {
      this.querySelector('[data-review-status]')?.replaceChildren(
        document.createTextNode('Ratings updated.'),
      );
    }
  }
}

class ReviewTrustStrip extends ReviewDataElement {
  applyPayload(payload) {
    this.querySelectorAll('[data-review-platform]').forEach((container) => {
      const platform = container.dataset.reviewPlatform;
      const record = platformRecord(payload, platform);
      const rating = toRating(record?.rating);
      const count = toCount(record?.reviewCount ?? record?.review_count);
      // Keep the nonnumeric server fallback if the shared record is incomplete.
      if (rating === null || count === null || count < 1) return;
      const platformName = platform === 'google' ? 'Google' : 'Trustpilot';
      const heading = container.querySelector('[data-review-trust-heading]');
      const countOutput = container.querySelector('[data-review-count]');
      if (heading) heading.textContent = `${rating.toFixed(1)} on ${platformName}`;
      if (countOutput) countOutput.textContent = formatCount(count, platform);
      updateStars(container, rating, platformName);
    });
  }
}

class ReviewSocialProof extends ReviewDataElement {
  connectedCallback() {
    this.setupPlatformPager();
    this.setupCarousel();
    super.connectedCallback();
  }

  setupPlatformPager() {
    if (this.platformPagerController) return;
    const track = this.querySelector('.cp-review-widget__platforms');
    const buttons = [...this.querySelectorAll('[data-platform-page]')];
    if (!track || !buttons.length) return;
    const controller = new AbortController();
    this.platformPagerController = controller;
    const { signal } = controller;
    const cardPosition = (card) => card.getBoundingClientRect().left - track.getBoundingClientRect().left;
    const sync = () => {
      const active = [...track.children].reduce((closest, card) => (
        Math.abs(cardPosition(card)) < Math.abs(cardPosition(closest)) ? card : closest
      ));
      buttons.forEach((button) => {
        if (button.dataset.platformPage === active.dataset.reviewPlatform) button.setAttribute('aria-current', 'page');
        else button.removeAttribute('aria-current');
      });
    };
    buttons.forEach((button) => button.addEventListener('click', () => {
      const card = track.querySelector(`[data-review-platform="${button.dataset.platformPage}"]`);
      if (!card) return;
      track.scrollTo({ left: cardPosition(card) + track.scrollLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }, { signal }));
    track.addEventListener('scroll', sync, { signal, passive: true });
    sync();
  }

  setupCarousel() {
    if (this.carouselController) return;
    const track = this.querySelector('[data-review-track]');
    const previous = this.querySelector('[data-review-previous]');
    const next = this.querySelector('[data-review-next]');
    const playback = this.querySelector('[data-review-playback]');
    if (!track || !previous || !next) return;
    const controller = new AbortController();
    this.carouselController = controller;
    const { signal } = controller;
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const autoplayEnabled = this.dataset.autoplay === 'true';
    // Preserve an explicit choice when this same section disconnects/reconnects.
    this.userPaused ??= motion.matches;
    let hovered = track.matches(':hover');
    let focused = track.contains(document.activeElement);
    let visible = !('IntersectionObserver' in window);
    const reviewDuration = Math.max(1000, Number(this.dataset.autoplayDelay) || 6500);
    const tickRate = 40;

    const step = () => {
      const firstCard = track.querySelector('.cp-review-card');
      const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 16;
      const reviewWidth = (firstCard?.getBoundingClientRect().width || track.clientWidth) + gap;
      track.scrollLeft += (reviewWidth / reviewDuration) * tickRate;
      if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 2) track.scrollLeft = 0;
    };

    const sync = () => {
      if (this.carouselController !== controller) return;
      const running = this.isConnected && autoplayEnabled && !this.userPaused && !hovered && !focused
        && visible && !document.hidden && track.scrollWidth > track.clientWidth + 2;
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
      track.classList.toggle('cp-review-widget__track--autoplay', running);
      track.setAttribute('aria-live', running ? 'off' : 'polite');
      if (playback) {
        playback.hidden = !autoplayEnabled;
        playback.textContent = this.userPaused ? 'Play reviews' : 'Pause reviews';
        playback.setAttribute('aria-label', this.userPaused ? 'Play automatic review scrolling' : 'Pause automatic review scrolling');
      }
      if (running) this.autoplayTimer = setInterval(step, tickRate);
    };
    this.syncCarousel = sync;
    const pauseByChoice = () => {
      this.userPaused = true;
      sync();
    };

    const move = (direction) => {
      pauseByChoice();
      const firstCard = track.querySelector('.cp-review-card');
      const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 16;
      const distance = (firstCard?.getBoundingClientRect().width || track.clientWidth) + gap;
      track.scrollBy({ left: distance * direction, behavior: motion.matches ? 'auto' : 'smooth' });
    };
    previous.addEventListener('click', () => move(-1), { signal });
    next.addEventListener('click', () => move(1), { signal });
    playback?.addEventListener('click', () => {
      this.userPaused = !this.userPaused;
      sync();
    }, { signal });
    track.addEventListener('mouseenter', () => { hovered = true; sync(); }, { signal });
    track.addEventListener('mouseleave', () => { hovered = false; sync(); }, { signal });
    track.addEventListener('focusin', () => { focused = true; sync(); }, { signal });
    track.addEventListener('focusout', (event) => {
      focused = track.contains(event.relatedTarget);
      if (!focused && this.pendingReviews) {
        const reviews = this.pendingReviews;
        this.pendingReviews = null;
        this.mergeReviews(reviews);
      }
      sync();
    }, { signal });
    track.addEventListener('pointerdown', pauseByChoice, { signal });
    track.addEventListener('click', pauseByChoice, { signal });
    document.addEventListener('visibilitychange', sync, { signal });
    motion.addEventListener('change', () => {
      if (motion.matches) this.userPaused = true;
      sync();
    }, { signal });

    if ('IntersectionObserver' in window) {
      this.reviewObserver = new IntersectionObserver((entries) => {
        visible = entries.some((entry) => entry.isIntersecting);
        sync();
      });
      this.reviewObserver.observe(track);
    }
    if ('ResizeObserver' in window) {
      this.reviewResizeObserver = new ResizeObserver(sync);
      this.reviewResizeObserver.observe(track);
    }
    sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.platformPagerController?.abort();
    this.platformPagerController = null;
    this.carouselController?.abort();
    this.carouselController = null;
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
    this.reviewObserver?.disconnect();
    this.reviewResizeObserver?.disconnect();
    this.syncCarousel = null;
    this.pendingReviews = null;
    this.querySelector('[data-review-track]')?.classList.remove('cp-review-widget__track--autoplay');
  }

  applyPayload(payload) {
    super.applyPayload(payload);
    const reviews = Array.isArray(payload?.reviews)
      ? payload.reviews
      : ['google', 'trustpilot'].flatMap((platform) => (
        Array.isArray(payload?.reviews?.[platform])
          ? payload.reviews[platform].map((review) => ({ ...review, platform: review.platform || platform }))
          : []
      ));
    // Only the platforms this widget shows (Trustpilot can be switched off in the section).
    const platforms = (this.dataset.platforms || 'google trustpilot').split(/\s+/);
    const usableReviews = reviews
      .filter((review) => review?.excerpt || review?.title)
      .filter((review) => platforms.includes(review?.platform === 'trustpilot' ? 'trustpilot' : 'google'))
      .sort((left, right) => reviewTimestamp(right?.publishedAt) - reviewTimestamp(left?.publishedAt));
    if (!usableReviews.length) return;

    const track = this.querySelector('[data-review-track]');
    if (!track) return;
    if (track.contains(document.activeElement)) {
      // Moving even the same focused node can lose keyboard focus. Wait until reading ends.
      this.pendingReviews = usableReviews;
      return;
    }
    this.mergeReviews(usableReviews);
  }

  mergeReviews(reviews) {
    const track = this.querySelector('[data-review-track]');
    if (!track) return;
    const fallbackUrls = {
      google: this.dataset.googleUrl || '',
      trustpilot: this.dataset.trustpilotUrl || '',
    };
    const showPhotos = this.dataset.showPhotos !== 'false';
    const logos = Object.fromEntries(['google', 'trustpilot'].map((platform) => [
      platform,
      this.querySelector(`[data-review-platform="${platform}"] .cp-review-widget__logo`),
    ]));
    const cards = [...track.querySelectorAll('[data-review-card-platform]')];
    const existing = new Map();
    cards.forEach((card) => {
      const key = reviewIdentityKey(card.dataset.reviewCardPlatform, card.dataset.reviewId);
      if (key) existing.set(key, card);
      // Only explicitly verified cross-posts share a card; names alone are not evidence.
      const alternatePlatform = card.dataset.reviewCardPlatform === 'google' ? 'trustpilot' : 'google';
      // A Google review has one ID per source (Places API, Business Profile), so the
      // setting may list several, separated by spaces or commas.
      String(card.dataset.reviewAlternateId || '').split(/[\s,]+/).forEach((alternateId) => {
        const alternateKey = reviewIdentityKey(alternatePlatform, alternateId);
        if (alternateKey) existing.set(alternateKey, card);
      });
    });
    const claimed = new Set();
    track.setAttribute('aria-live', 'off');
    // A feed can contain only recent reviews. Preserve the entire restored catalogue.
    for (const review of reviews) {
      const platform = review?.platform === 'trustpilot' ? 'trustpilot' : 'google';
      const id = String(review?.id || '').trim();
      const key = reviewIdentityKey(platform, id);
      // Saved cards hold Places review IDs. Google's newest-first list and the
      // Business Profile API give the same review another ID, so those match a
      // saved card by name and quote instead, and the card keeps its own ID.
      const feedOnlyId = platform === 'google'
        && (/\/legacy\//.test(id) || /^accounts\/[^/]+\/locations\/[^/]+\/reviews\//.test(id));
      const savedCard = existing.get(key) || cards.find((card) => !claimed.has(card) && matchesLegacyReview(
        card, platform, review?.reviewerName, review?.excerpt || review?.title, feedOnlyId,
      )) || (feedOnlyId ? findSavedReviewerCard(cards, platform, review, claimed) : null);
      if (savedCard) {
        claimed.add(savedCard);
        if (key && !feedOnlyId && savedCard.dataset.reviewCardPlatform === platform) {
          savedCard.dataset.reviewId = id;
          existing.set(key, savedCard);
        }
        // Saved cards only know the month; the feed's exact day orders reviews within a month.
        if (savedCard.dataset.reviewCardPlatform === platform && reviewTimestamp(review?.publishedAt)) {
          savedCard.dataset.reviewPublishedAt = review.publishedAt;
        }
        // Saved cards hold only text; the feed is the only source of the customer's photo.
        if (showPhotos && savedCard.dataset.reviewCardPlatform === platform) {
          attachReviewPhoto(savedCard, review, reviewerFirstName(
            savedCard.querySelector('.cp-review-card__reviewer')?.textContent, `${platform === 'trustpilot' ? 'Trustpilot' : 'Google'} customer`,
          ));
        }
        continue;
      }
      const card = createReviewCard(review, fallbackUrls, logos, { showPhotos });
      track.append(card);
      cards.push(card);
      if (key) existing.set(key, card);
    }
    sortRenderedCards(track);
    this.querySelector('[data-review-empty]')?.remove();
    this.querySelector('[data-review-region]')?.removeAttribute('hidden');
    this.syncCarousel?.();
  }
}

if (!customElements.get('review-social-proof')) {
  customElements.define('review-social-proof', ReviewSocialProof);
}
if (!customElements.get('review-rating-badge')) {
  customElements.define('review-rating-badge', ReviewDataElement);
}
if (!customElements.get('review-trust-strip')) {
  customElements.define('review-trust-strip', ReviewTrustStrip);
}
